import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ImageUploadConversionHub = () => {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [convertedFiles, setConvertedFiles] = useState([]);
  const [errors, setErrors] = useState([]);

  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const validateFile = (file) => {
    const validTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/bmp',
      'image/tiff',
      'image/gif'
    ];

    if (!validTypes.includes(file.type.toLowerCase())) {
      return `${file.name}: Unsupported format. Please use JPG, PNG, BMP, TIFF, or GIF.`;
    }

    if (file.size > maxFileSize) {
      return `${file.name}: File too large. Maximum size is 10MB.`;
    }

    return null;
  };

  const convertToWebP = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          const webpFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.webp'), {
            type: 'image/webp'
          });

          resolve({
            originalName: file.name,
            convertedFile: webpFile,
            downloadUrl: URL.createObjectURL(blob),
            size: blob.size
          });
        }, 'image/webp', 0.8);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const processFiles = useCallback(async (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    const fileErrors = [];

    fileArray.forEach(file => {
      const error = validateFile(file);
      if (error) {
        fileErrors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    if (fileErrors.length > 0) {
      setErrors(fileErrors);
      setTimeout(() => setErrors([]), 5000);
    }

    if (validFiles.length > 0) {
      setIsProcessing(true);

      try {
        const converted = await Promise.all(
          validFiles.map(file => convertToWebP(file))
        );

        setConvertedFiles(prev => [...prev, ...converted]);
      } catch (error) {
        setErrors(['Conversion failed. Please try again.']);
        setTimeout(() => setErrors([]), 5000);
      } finally {
        setIsProcessing(false);
      }
    }
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => {
      const newCounter = prev - 1;
      if (newCounter === 0) {
        setIsDragOver(false);
      }
      return newCounter;
    });
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragCounter(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  }, [processFiles]);

  const handleFileSelect = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    e.target.value = '';
  }, [processFiles]);

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownload = (convertedFile) => {
    const link = document.createElement('a');
    link.href = convertedFile.downloadUrl;
    link.download = convertedFile.originalName.replace(/\.[^/.]+$/, '.webp');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadAll = () => {
    convertedFiles.forEach(file => {
      setTimeout(() => handleDownload(file), 100);
    });
  };

  const handleClear = () => {
    convertedFiles.forEach(file => {
      URL.revokeObjectURL(file.downloadUrl);
    });
    setConvertedFiles([]);
    setErrors([]);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-6">
      <motion.div
        className="w-full max-w-4xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Enhanced Header */}
        <motion.div
          className="text-center mb-12"
          variants={itemVariants}
        >
          <motion.div
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-6 shadow-lg"
            whileHover={{ scale: 1.05, rotate: 5 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <img src="/assets/images/android-chrome-192x192.png" alt="WebP Logo" width={192} height={192} className="rounded-sm" />
          </motion.div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-4">
            WebP Converter
          </h1>
          <p className="text-xl text-slate-600 font-medium max-w-2xl mx-auto leading-relaxed">
            Transform your images into efficient WebP format with our fast, secure converter
          </p>
        </motion.div>

        {/* Error Messages with Animation */}
        <AnimatePresence>
          {errors.length > 0 && (
            <motion.div
              className="mb-8 bg-red-50 border border-red-200 rounded-2xl p-6 shadow-sm"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <Icon name="AlertTriangle" size={18} className="text-red-600" />
                </div>
                <h3 className="font-semibold text-red-800">Upload Errors</h3>
              </div>
              <ul className="text-sm text-red-700 space-y-2 ml-11">
                {errors.map((error, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    • {error}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Enhanced Upload Zone */}
        <motion.div
          variants={itemVariants}
          className="mb-12"
        >
          <motion.div
            className={`
              relative overflow-hidden
              border-2 border-dashed rounded-3xl p-16 text-center 
              transition-all duration-300 ease-out cursor-pointer
              bg-white/70 backdrop-blur-sm shadow-xl
              ${isDragOver
                ? 'border-blue-400 bg-blue-50/80 shadow-2xl scale-[1.02]'
                : 'border-slate-300 hover:border-blue-300 hover:bg-slate-50/50 hover:shadow-2xl hover:scale-[1.01]'
              }
              ${isProcessing ? 'opacity-60 pointer-events-none' : ''}
            `}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleBrowseClick}
            whileHover={{ y: -2 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/jpg,image/png,image/bmp,image/tiff,image/gif"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isProcessing}
            />

            {/* Animated Upload Icon */}
            <motion.div
              className={`
                w-24 h-24 mx-auto mb-8 rounded-full flex items-center justify-center transition-all duration-300
                ${isDragOver
                  ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg'
                  : 'bg-gradient-to-br from-slate-100 to-slate-200 text-slate-600 hover:from-blue-100 hover:to-blue-200 hover:text-blue-600'
                }
              `}
              whileHover={{ scale: 1.1, rotate: 10 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <Icon
                name={isDragOver ? 'Download' : isProcessing ? 'Loader2' : 'Upload'}
                size={36}
                strokeWidth={2}
                className={isProcessing ? 'animate-spin' : ''}
              />
            </motion.div>

            <motion.h3
              className="text-2xl font-bold text-slate-800 mb-3"
              animate={{ scale: isDragOver ? 1.05 : 1 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              {isDragOver ? 'Drop your images here!' : isProcessing ? 'Converting...' : 'Drop or click to upload'}
            </motion.h3>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {isDragOver
                ? 'Release to start converting to WebP' : 'Supports JPG, PNG, BMP, TIFF, and GIF files up to 10MB each'
              }
            </p>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="default"
                size="xl"
                iconName={isProcessing ? "Loader2" : "Upload"}
                iconPosition="left"
                disabled={isProcessing}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl text-lg px-8 py-4 rounded-xl transition-all duration-300"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBrowseClick();
                }}
              >
                {isProcessing ? 'Converting Images...' : 'Choose Files'}
              </Button>
            </motion.div>

            {/* Processing Overlay */}
            <AnimatePresence>
              {isProcessing && (
                <motion.div
                  className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-3xl flex items-center justify-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="flex items-center space-x-4 text-slate-700">
                    <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xl font-semibold">Converting to WebP...</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>

        {/* Enhanced Download Section */}
        <AnimatePresence>
          {convertedFiles.length > 0 && (
            <motion.div
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-slate-200"
              initial={{ opacity: 0, y: 20, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: -20, height: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-800">
                      Conversion Complete!
                    </h3>
                    <p className="text-slate-600">
                      {convertedFiles.length} file{convertedFiles.length !== 1 ? 's' : ''} ready for download
                    </p>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="default"
                      size="lg"
                      iconName="Download"
                      iconPosition="left"
                      onClick={handleDownloadAll}
                      className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-lg hover:shadow-xl"
                    >
                      Download All
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="outline"
                      size="lg"
                      iconName="X"
                      iconPosition="left"
                      onClick={handleClear}
                      className="border-slate-300 hover:bg-slate-50"
                    >
                      Clear
                    </Button>
                  </motion.div>
                </div>
              </div>

              <div className="space-y-3">
                {convertedFiles.map((file, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center justify-between p-5 bg-gradient-to-r from-slate-50 to-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.01, x: 4 }}
                  >
                    <div className="flex items-center space-x-4">
                      <motion.div
                        className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center"
                        whileHover={{ rotate: 5 }}
                      >
                        <Icon name="FileImage" size={20} className="text-blue-600" />
                      </motion.div>
                      <div>
                        <p className="font-semibold text-slate-800 text-lg">
                          {file.originalName.replace(/\.[^/.]+$/, '.webp')}
                        </p>
                        <p className="text-slate-600">
                          {(file.size / 1024).toFixed(1)} KB • WebP Format
                        </p>
                      </div>
                    </div>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="default"
                        size="lg"
                        iconName="Download"
                        iconPosition="left"
                        onClick={() => handleDownload(file)}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg"
                      >
                        Download WebP
                      </Button>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default ImageUploadConversionHub;